import React from "react";
import { Button } from "../../Button";
import { formatBRL } from "../InvoiceSplitPreviewModal";
import {
  displayCivilDate,
  displayTransactionStatus,
  displayWeeklyPeriod,
  hasFinalInvoiceStatus,
  isWrittenOffWeek,
} from "../../../routes/pages/org.utils";
import type { WeeklyCollection, WeeklyPeriod } from "../../../routes/pages/org.utils";
import type { WeeklyBilling, WeeklyStatementSummary } from "../../../routes/pages/org.weekly";
import "./WeeklyChargeCard.css";

export type WeeklyChargeCardProps = {
  summary: WeeklyStatementSummary;
  period?: WeeklyPeriod;
  invoiceStatus: string;
  billing?: WeeklyBilling;
  collection?: WeeklyCollection;
  /** The company's fixed weekly cap; absent when none is set or it is unlimited. */
  weeklyLimitCents?: number;
  /** The reviewed Pix fee preparation adds, shown while the week carries none yet. */
  processingFeeCents?: number;
  onOpenStatement: () => void;
};

type Tone = "success" | "warning" | "danger" | "info" | "neutral";

const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

/** "qua, 04/11" for a civil date (YYYY-MM-DD). */
function weekdayDate(civil: string): string {
  const day = new Date(`${civil}T00:00:00Z`).getUTCDay();
  return `${WEEKDAYS[day]}, ${displayCivilDate(civil).slice(0, 5)}`;
}

/** A period ends on the day after its last day, like displayWeeklyPeriod reads it. */
function lastDayOf(period: WeeklyPeriod): string {
  const last = new Date(`${period.endsOn}T00:00:00Z`);
  last.setUTCDate(last.getUTCDate() - 1);
  return last.toISOString().slice(0, 10);
}

function statusTone(
  invoiceStatus: string,
  billing?: WeeklyBilling,
  collection?: WeeklyCollection,
): Tone {
  if (isWrittenOffWeek({ invoiceStatus, weeklyBilling: billing })) return "neutral";
  if (invoiceStatus === "paid" || invoiceStatus === "compensated") return "success";
  if (invoiceStatus === "failed" || invoiceStatus === "dispute-lost") return "danger";
  if (collection?.phase === "stopped") return "danger";
  if (invoiceStatus === "weekly-recovery" || collection?.phase === "recovery") return "warning";
  if (billing?.state === "held" || billing?.state === "deferred") return "warning";
  return "info";
}

/** Which of the four steps the week is on: forming, closed, debit, paid. */
function currentStep(invoiceStatus: string, collection?: WeeklyCollection): number {
  if (hasFinalInvoiceStatus(invoiceStatus)) return 3;
  if (collection) return 2;
  if (invoiceStatus === "weekly-open") return 0;
  return 1;
}

/**
 * The week being charged, at the top of /organizacao/pagamentos: what it adds
 * up to so far, how it splits, how much of the weekly cap it uses and where
 * the charge stands. Read-only; the statement opens from it.
 * @story layout padded
 * @story args { invoiceStatus: "weekly-open", summary: { serviceAmountCents: 431240, negotiationTopupAmountCents: 50000, totalAmountCents: 481240, groupCount: 4 }, period: { startsOn: "2026-10-26", endsOn: "2026-11-02", dueOn: "2026-11-04" }, billing: { policyVersion: 2, minimumNetServicesCents: 100, state: "open", disposition: "accumulation", ownServiceAmountCents: 431240, carriedServiceAmountCents: 0, netServiceAmountCents: 431240 }, weeklyLimitCents: 2500000, processingFeeCents: 350 }
 * @story state Unlimited { weeklyLimitCents: undefined }
 * @story state WithCredit { summary: { serviceAmountCents: 431240, negotiationTopupAmountCents: 50000, customerCreditAmountCents: -12000, totalAmountCents: 469240, groupCount: 4 }, billing: { policyVersion: 2, minimumNetServicesCents: 100, state: "open", disposition: "accumulation", ownServiceAmountCents: 431240, carriedServiceAmountCents: 0, netServiceAmountCents: 419240 } }
 * @story state CarriedCosts { billing: { policyVersion: 2, minimumNetServicesCents: 100, state: "open", disposition: "accumulation", ownServiceAmountCents: 400000, carriedServiceAmountCents: 31240, netServiceAmountCents: 431240 } }
 * @story state OverLimit { weeklyLimitCents: 300000 }
 * @story state Prepared { invoiceStatus: "weekly-closed", billing: { policyVersion: 2, minimumNetServicesCents: 100, state: "closed", disposition: "accumulation", ownServiceAmountCents: 431240, carriedServiceAmountCents: 0, netServiceAmountCents: 431240, expectedChargeOn: "2026-11-04" }, summary: { serviceAmountCents: 431240, processingFeeAmountCents: 350, totalAmountCents: 431590, groupCount: 4 } }
 * @story state Paid { invoiceStatus: "paid", billing: { policyVersion: 2, minimumNetServicesCents: 100, state: "paid", disposition: "accumulation", ownServiceAmountCents: 431240, carriedServiceAmountCents: 0, netServiceAmountCents: 431240 } }
 * @story state LegacyWithoutBilling { billing: undefined }
 */
export function WeeklyChargeCard({
  summary,
  period,
  invoiceStatus,
  billing,
  collection,
  weeklyLimitCents,
  processingFeeCents,
  onOpenStatement,
}: WeeklyChargeCardProps) {
  const titleId = React.useId();
  const total = summary.totalAmountCents;
  const open = invoiceStatus === "weekly-open";
  const step = currentStep(invoiceStatus, collection);
  // The same forecast rule as WeeklyPixSummary: a policy-2 week is dated only
  // by Payments' expectedChargeOn, the calendar due date serves legacy records
  // alone, and a final, forgiven or contributing week has no forecast at all.
  const final =
    hasFinalInvoiceStatus(invoiceStatus) ||
    isWrittenOffWeek({ invoiceStatus, weeklyBilling: billing });
  const expected =
    final || billing?.disposition === "contribution"
      ? undefined
      : billing
        ? billing.expectedChargeOn
        : period?.dueOn;
  const usage =
    weeklyLimitCents && weeklyLimitCents > 0
      ? Math.min(100, Math.round((total * 100) / weeklyLimitCents))
      : undefined;
  const steps = [
    {
      label: "Em formação",
      detail: period
        ? `Somando o consumo até ${weekdayDate(lastDayOf(period))}`
        : "Somando o consumo",
    },
    { label: "Semana fechada", detail: "A cobrança é preparada com a tarifa Pix" },
    {
      label: "Débito previsto",
      detail: expected
        ? `${weekdayDate(expected)}, pelo Pix Automático`
        : final
          ? "Pelo Pix Automático"
          : "Pelo Pix Automático, com data informada após o fechamento",
    },
    { label: "Pago", detail: "As notas fiscais ficam no extrato" },
  ];

  return (
    <section
      className="weeklyChargeCard"
      aria-labelledby={titleId}
      data-testid="weekly-charge-card"
    >
      <div className="overview">
        <div className="headline">
          <div className="eyebrow">
            <span>{open ? "Cobrança desta semana" : "Última cobrança semanal"}</span>
            <span className={`status is-${statusTone(invoiceStatus, billing, collection)}`}>
              {displayTransactionStatus({
                invoiceStatus,
                weeklyCollection: collection,
                weeklyBilling: billing,
              })}
            </span>
          </div>
          <h2 className="period" id={titleId}>
            {period ? `Semana de ${displayWeeklyPeriod(period)}` : "Período não informado"}
          </h2>
          <p className="total" data-testid="weekly-charge-total">
            {formatBRL(total)}
          </p>
          {open && (
            <p className="note">
              Acumulado até agora. Os valores podem mudar até o fechamento da semana.
            </p>
          )}
          {usage !== undefined && weeklyLimitCents !== undefined && (
            <div className={`limit${usage >= 100 ? " is-full" : ""}`}>
              <div className="limitLabel">
                <span>Uso do limite semanal</span>
                <span>
                  <strong>{formatBRL(total)}</strong> de {formatBRL(weeklyLimitCents)}
                </span>
              </div>
              <div
                className="limitTrack"
                role="meter"
                aria-label="Uso do limite semanal"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={usage}
                aria-valuetext={`${usage}% do limite semanal`}
                style={{ "--weekly-limit-usage": `${usage}%` } as React.CSSProperties}
              >
                <span className="limitFill" />
              </div>
            </div>
          )}
          <div className="actions">
            <Button callback={onOpenStatement}>Ver extrato da semana</Button>
          </div>
        </div>

        <dl className="breakdown">
          <div>
            <dt>Serviços</dt>
            <dd className="amount">{formatBRL(summary.serviceAmountCents)}</dd>
            <dd className="hint">
              {billing && billing.carriedServiceAmountCents > 0
                ? "Inclui custos de semanas anteriores"
                : "Consumo das campanhas"}
            </dd>
          </div>
          {summary.negotiationTopupAmountCents !== undefined && (
            <div>
              <dt>Recarga do saldo</dt>
              <dd className="amount">{formatBRL(summary.negotiationTopupAmountCents)}</dd>
              <dd className="hint">Recarga automática do Saldo de Negociações</dd>
            </div>
          )}
          {summary.customerCreditAmountCents !== undefined && (
            <div>
              <dt>Crédito da empresa</dt>
              {/* Payments sends the credit signed (always negative), like
                  WeeklyGroupedStatement renders it. */}
              <dd className="amount">{formatBRL(summary.customerCreditAmountCents)}</dd>
              <dd className="hint">Abatido do total</dd>
            </div>
          )}
          <div className={summary.processingFeeAmountCents === undefined ? "is-pending" : ""}>
            <dt>Tarifa Pix</dt>
            <dd className="amount">
              {summary.processingFeeAmountCents !== undefined
                ? formatBRL(summary.processingFeeAmountCents)
                : processingFeeCents !== undefined
                  ? formatBRL(processingFeeCents)
                  : "A definir"}
            </dd>
            <dd className="hint">
              {summary.processingFeeAmountCents !== undefined
                ? "Incluída uma única vez"
                : "Entra na preparação da cobrança"}
            </dd>
          </div>
        </dl>
      </div>

      <ol className="steps" aria-label="Etapas da cobrança">
        {steps.map((item, index) => (
          <li
            key={item.label}
            className={index < step ? "is-done" : index === step ? "is-current" : ""}
            aria-current={index === step ? "step" : undefined}
          >
            <span className="stepLabel">{item.label}</span>
            <span className="stepDetail">{item.detail}</span>
          </li>
        ))}
      </ol>

      <details className="explainer">
        <summary>Como a cobrança funciona</summary>
        <ol>
          <li>
            <strong>Somamos o consumo.</strong> Durante a semana juntamos o que as campanhas
            consumiram, menos os créditos elegíveis da empresa.
          </li>
          <li>
            <strong>Preparamos a cobrança.</strong> Quando a semana fecha, a tarifa Pix entra uma
            única vez. Abaixo de R$ 1,00 de serviços, antes da tarifa, os custos ficam para uma
            próxima semana.
          </li>
          <li>
            <strong>Debitamos pelo Pix Automático.</strong> O débito é previsto para a quarta-feira
            seguinte, dentro do limite semanal. A data depende do processamento do banco.
          </li>
        </ol>
      </details>
    </section>
  );
}
